/** @format */

import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import styled from 'styled-components'
import { CountContext } from '../index'
import ToolBar from './ToolBar'
import GraphX6 from './Graph'
import { GraphX6Ref } from 'src/components/GraphX6/types'
import { handleNodeData, handleRouterData, NodeTypeEnum } from '../../container'
import EdgeDrawer from './EdgeDrawer'
import NodeDrawer from './NodeDrawer'
import { Cell, Node } from '@antv/x6'
import { NodePreDecessorsProps } from './NodeDrawer'
import { cloneDeep } from 'lodash'

interface SceneDesignerProps {
  className?: string
  ref?: React.Ref<GraphX6Ref>
}

const SceneDesigner: React.FC<SceneDesignerProps> = forwardRef(({ className }, ref) => {
  const { editable, sceneInfo } = useContext(CountContext)
  // 画布ref
  const graphRef = useRef<GraphX6Ref>(null)
  // 节点抽屉
  const [nodeDrawerVisible, setNodeDrawerVisible] = useState(false)
  const [nodeDrawerData, setNodeDrawerData] = useState<Cell.Properties>()
  const [nodePreDecessors, setNodePreDecessors] = useState<NodePreDecessorsProps[]>([])
  const [allEndNodes, setAllEndNodes] = useState<string[]>([])
  // 边抽屉
  const [edgeDrawerVisible, setEdgeDrawerVisible] = useState(false)
  const [edgeDrawerData, setEdgeDrawerData] = useState<Cell.Properties>()
  const [edgeSourceInputs, setEdgeSourceInputs] = useState<string[]>([])

  // 撤销/重做
  const [historyChange, setHistoryChange] = useState({
    canRedo: false,
    canUndo: false
  })

  // 图形初始化完成回调
  const handleGraphReady = useCallback(
    graph => {
      // 可以在这里执行需要 graph 实例的操作
      graph.on('cell:click', event => {
        const cell = event.cell
        const currentPreDecessors = graphRef.current?.graph?.getPredecessors(cell) || []
        if (cell?.shape === 'edge') {
          if (currentPreDecessors?.[0]?.data?.type === 'GATEWAY') {
            console.log('cell', cell.data)
            setEdgeDrawerVisible(true)
            setEdgeDrawerData(cell)
            setEdgeSourceInputs(currentPreDecessors[0].data.outputs)
          }
        } else if (!['START', 'PARALLEL_GATEWAY'].includes(cell?.data?.type)) {
          setNodePreDecessors(
            currentPreDecessors.reduce((acc, cur) => {
              if (cur.data.inputs || cur.data.outputs) {
                acc.push({
                  id: cur.id,
                  name: cur.data.name,
                  input: cur.data.inputs?.map(item => item.Key),
                  output: cur.data.outputs?.map(item => item)
                })
                return acc
              }
              return acc
            }, []) || []
          )
          setAllEndNodes(
            (graphRef.current?.graph?.toJSON()?.cells || [])
              .filter((item: Node.Properties) => item.data?.type === 'END')
              .filter((item: Node.Properties) => item.id !== cell.id)
              .map((mapItem: Node.Properties) => mapItem.attrs?.text?.text as string)
          )
          // 将节点类型赋值给节点名称，防止节点名称为空
          if (!cell?.data?.name) {
            cell.data.name = NodeTypeEnum[cell.data.type]
          }
          setNodeDrawerVisible(true)
          setNodeDrawerData(cell)
        }
      })

      if (sceneInfo) {
        graph?.fromJSON({
          nodes: handleNodeData(sceneInfo?.Workflow?.Nodes || []),
          edges: handleRouterData(sceneInfo?.Workflow?.Routes || [])
        })
      } else {
        graph?.clearCells()
      }
      graph?.centerContent()
    },
    [sceneInfo]
  )

  useEffect(() => {
    if (graphRef?.current?.graph) {
      graphRef.current.graph.fromJSON({
        nodes: handleNodeData(sceneInfo?.Workflow?.Nodes || []),
        edges: handleRouterData(sceneInfo?.Workflow?.Routes || [])
      })
      graphRef.current.graph?.centerContent()
    }
  }, [sceneInfo])

  useImperativeHandle(ref, () => {
    return {
      graph: graphRef?.current?.graph
    }
  })

  return (
    <div className={`${className} ${editable ? 'editable' : ''}`}>
      {editable && <ToolBar historyChange={historyChange} graphRef={graphRef} />}
      <div
        className="graph_container"
        style={{ height: editable ? 'calc(100vh - 236px)' : 'calc(100vh - 216px)' }}
      >
        <GraphX6
          ref={graphRef}
          editable={editable}
          onGraphReady={handleGraphReady}
          onHistoryChange={(canRedo, canUndo) => {
            setHistoryChange({ canRedo, canUndo })
          }}
        />
      </div>
      <EdgeDrawer
        visible={edgeDrawerVisible}
        onClose={() => {
          setEdgeDrawerVisible(false)
        }}
        edgeData={edgeDrawerData}
        edgeSourceInputs={edgeSourceInputs}
        onChange={edgeValues => {
          if (edgeValues.id) {
            const graph = graphRef?.current?.graph
            if (!graph) return

            // 通过 ID 获取实际的 edge cell 对象
            const cell = graph.getCellById(edgeValues.id)
            if (cell && cell.isEdge()) {
              const newData = cloneDeep(edgeValues)
              cell.prop('data', newData)
              // 更新边的标签
              cell.setLabels([
                {
                  attrs: {
                    label: {
                      fill: 'rgba(0,0,0,0.65)',
                      fontSize: 12,
                      text: edgeValues.name
                    }
                  }
                }
              ])
            }
            setEdgeDrawerData(undefined)
            setEdgeDrawerVisible(false)
          }
        }}
      />
      <NodeDrawer
        visible={nodeDrawerVisible}
        onClose={() => {
          setNodeDrawerVisible(false)
        }}
        nodeData={nodeDrawerData}
        nodePreDecessors={nodePreDecessors}
        allEndNodes={allEndNodes}
        onChange={nodeValues => {
          const graph = graphRef?.current?.graph
          if (!graph) return

          // 通过 ID 获取实际的 cell 对象
          const cell = graph.getCellById(nodeValues.id)
          if (cell && cell.isNode()) {
            const newData = cloneDeep(nodeValues)
            cell.prop('data', newData)
            // 更新节点标签文本
            cell.setAttrs({
              text: {
                text: nodeValues.name
              }
            })
            // 更新节点 label（如果存在 label 属性）
            if (cell.prop('label') !== undefined) {
              cell.prop('label', nodeValues.name)
            }
          }

          setNodeDrawerVisible(false)
          setNodeDrawerData(undefined)
          setNodePreDecessors([])
          setAllEndNodes([])
        }}
      />
    </div>
  )
})

export default styled(SceneDesigner)`
  /* border: 1px solid #e5e5e5; */
  &.editable {
    padding-top: 8px;
    .graph_container {
      padding-top: 4px;
    }
  }
`
